import { useState } from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { ApiError } from '@/auth/api';
import { PrimaryButton } from '@/components/PrimaryButton';
import { cancelInternalReservation } from '@/features/operations/api';
import type { ActiveReservation, DeviceDetail } from '@/features/operations/types';

/**
 * Cancel a reservation from the device sheet (VEN-4, P-C5). The reason is required: it stays in
 * the device's notes, with who cancelled and when. The buyer is told by mail that the
 * reservation is over, without the reason, which is written for the team.
 *
 * When the reservation changed meanwhile (someone concreted or cancelled it), the dialog stays
 * open saying so; closing it asks the sheet to read the device again.
 */
export function CancelReservationDialog({
  open,
  token,
  deviceId,
  reservation,
  onClose,
  onCancelled,
}: {
  open: boolean;
  token: string;
  deviceId: string;
  reservation: Pick<ActiveReservation, 'id' | 'reference' | 'buyer'>;
  /// `stale` is true when the reservation turned out to have changed.
  onClose: (stale: boolean) => void;
  onCancelled: (device: DeviceDetail) => void;
}) {
  const [reason, setReason] = useState('');
  const [reasonError, setReasonError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [stale, setStale] = useState(false);
  const [requestError, setRequestError] = useState<string | null>(null);

  const reset = () => {
    setReason('');
    setReasonError(null);
    setStale(false);
    setRequestError(null);
  };

  const close = () => {
    if (isSaving) return;
    const wasStale = stale;
    reset();
    onClose(wasStale);
  };

  const handleCancel = async () => {
    if (!reason.trim()) {
      setReasonError('Contá por qué se cancela la reserva.');
      return;
    }

    setIsSaving(true);
    setRequestError(null);

    try {
      const updated = await cancelInternalReservation(
        token,
        deviceId,
        reservation.id,
        reason.trim(),
      );
      reset();
      onCancelled(updated);
    } catch (cause) {
      if (cause instanceof ApiError && (cause.status === 409 || cause.status === 404)) {
        setStale(true);
      } else {
        setRequestError('No pudimos cancelar la reserva. Volvé a intentar.');
      }
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={close} maxWidth="sm" fullWidth>
      <DialogTitle>Cancelar la reserva #{reservation.reference}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          {stale ? (
            <Alert severity="error">
              La reserva ya no está activa: otra persona la concretó o la canceló recién. Cerrá este
              diálogo para ver cómo quedó el equipo.
            </Alert>
          ) : null}
          {requestError ? <Alert severity="error">{requestError}</Alert> : null}
          <Typography variant="body2">
            El equipo vuelve a <strong>Publicado</strong> y le avisamos por mail a{' '}
            <strong>{reservation.buyer.name}</strong> que la reserva quedó cancelada.
          </Typography>
          <TextField
            autoFocus
            fullWidth
            multiline
            minRows={2}
            required
            label="Motivo"
            value={reason}
            disabled={isSaving || stale}
            error={Boolean(reasonError)}
            helperText={
              reasonError ??
              'Queda en las notas del equipo, con tu usuario y la fecha. No se le manda al comprador.'
            }
            onChange={(event) => {
              setReason(event.target.value);
              setReasonError(null);
            }}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button color="inherit" disabled={isSaving} onClick={close}>
          {stale ? 'Cerrar' : 'Volver'}
        </Button>
        {stale ? null : (
          <PrimaryButton disabled={isSaving} onClick={() => void handleCancel()}>
            {isSaving ? 'Cancelando...' : 'Cancelar reserva'}
          </PrimaryButton>
        )}
      </DialogActions>
    </Dialog>
  );
}
